import React, { useState, useEffect, useCallback } from 'react';
import { getPendingFreestylers, getAllFreestylers, approveFreestyler, rejectFreestyler, updateFreestyler, deleteFreestyler } from '../services/api';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from '../hooks/use-toast';
import {
  Shield, Lock, Check, X, Edit3, Trash2, User, Clock, CheckCircle,
  XCircle, Loader2, Eye, MapPin, Instagram, Music2, RefreshCw, AlertTriangle,
  Mail, Download, Copy, Send, MessageCircle, Bot, BarChart3, TrendingUp, Users,
  Trophy, ArrowUp, ArrowDown, Plus, Film, Crown, Handshake
} from 'lucide-react';
import { io as socketIO } from 'socket.io-client';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { DEFAULT_EDITIONS } from '../components/EditionsHistoryModal';
import { logError } from '../lib/logError';
import InfoField from '../components/admin/InfoField';
import ProfileList from '../components/admin/ProfileList';
import EmailsPanel from '../components/admin/EmailsPanel';
import InstagramPanel from '../components/admin/InstagramPanel';
import HomeContentPanel from '../components/admin/HomeContentPanel';
import WallOfFamePanel from '../components/admin/WallOfFamePanel';
import SponsorsPanel, { DEFAULT_SPONSORS } from '../components/admin/SponsorsPanel';
import { DEFAULT_WALL_OF_FAME } from '../data/wallOfFame';
import { withUids } from '../lib/uid';
import StatsPanel from '../components/admin/StatsPanel';
import ChatLivePanel from '../components/admin/ChatLivePanel';

// Admin PIN validation is done on backend for security
const AdminPage = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [pin, setPin] = useState('');
  const [pending, setPending] = useState([]);
  const [allProfiles, setAllProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [actionLoading, setActionLoading] = useState(null);
  const [emailsData, setEmailsData] = useState(null);
  const [emailsLoading, setEmailsLoading] = useState(false);
  const [emailFilter, setEmailFilter] = useState('all');
  const [emailSearch, setEmailSearch] = useState('');
  const [igShortcodes, setIgShortcodes] = useState(['', '', '', '']);
  const [igSaving, setIgSaving] = useState(false);
  const [homeFinalists, setHomeFinalists] = useState([]);
  const [homeShorts, setHomeShorts] = useState([]);
  const [homeEditions, setHomeEditions] = useState([]);
  const [homeSettings, setHomeSettings] = useState({ finalists_enabled: false, finalists_title: '', featured_video_url: '', featured_video_title: '', show_registration_btn: false, show_prizes_btn: false, countdown_enabled: true, countdown_date: '', countdown_label: '' });
  const [homeSaving, setHomeSaving] = useState(false);
  const [homeLoaded, setHomeLoaded] = useState(false);
  const [wallItems, setWallItems] = useState([]);
  const [wallLoaded, setWallLoaded] = useState(false);
  const [wallSaving, setWallSaving] = useState(false);
  const [sponsorItems, setSponsorItems] = useState([]);
  const [sponsorsLoaded, setSponsorsLoaded] = useState(false);
  const [sponsorsSaving, setSponsorsSaving] = useState(false);
  const [chatRooms, setChatRooms] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [userTypingChatId, setUserTypingChatId] = useState(null);
  const [chatSearch, setChatSearch] = useState('');
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsDays, setAnalyticsDays] = useState(30);
  const [analyticsRegion, setAnalyticsRegion] = useState('');
  const [analyticsYear, setAnalyticsYear] = useState(0);
  const [freestylerSearch, setFreestylerSearch] = useState('');
  const [freestylerCrewFilter, setFreestylerCrewFilter] = useState('');
  const [notifPermission, setNotifPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );
  const adminSocketRef = React.useRef(null);
  const adminTypingTimerRef = React.useRef(null);
  const userTypingTimerRef = React.useRef(null);
  const originalTitleRef = React.useRef('');
  const blinkIntervalRef = React.useRef(null);
  const faviconRef = React.useRef(null);

  useEffect(() => {
    const saved = sessionStorage.getItem('tp_admin_auth');
    if (saved === 'true') setAuthenticated(true);
  }, []);


  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('pin', pin);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/auth`, {
        method: 'POST',
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.authenticated) {
          setAuthenticated(true);
          sessionStorage.setItem('tp_admin_auth', 'true');
          sessionStorage.setItem('tp_admin_pin', pin);
          toast({ title: "Accesso autorizzato", description: "Benvenuto nel pannello admin" });
        }
      } else {
        toast({ title: "PIN errato", description: "Riprova con il PIN corretto", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Errore di connessione", description: "Impossibile verificare il PIN", variant: "destructive" });
    }
  };

  const handleLogout = () => {
    setAuthenticated(false);
    sessionStorage.removeItem('tp_admin_auth');
    sessionStorage.removeItem('tp_admin_pin');
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [pendingData, allData] = await Promise.all([
        getPendingFreestylers(),
        getAllFreestylers()
      ]);
      setPending(pendingData);
      setAllProfiles(allData);
    } catch (e) {
      logError('[admin] caricamento profili fallito', e);
      toast({ title: "Errore", description: "Impossibile caricare i dati", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authenticated) loadData();
  }, [authenticated, loadData]);

  const loadEmails = async () => {
    setEmailsLoading(true);
    try {
      const savedPin = sessionStorage.getItem('tp_admin_pin') || pin;
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/emails-list?pin=${encodeURIComponent(savedPin)}`
      );
      if (!res.ok) throw new Error('unauthorized');
      const data = await res.json();
      setEmailsData(data);
    } catch {
      toast({ title: "Errore", description: "Impossibile caricare le email. Riaccedi.", variant: "destructive" });
    } finally {
      setEmailsLoading(false);
    }
  };

  const filteredEmails = React.useMemo(() => {
    if (!emailsData) return [];
    let list = emailsData.emails;
    if (emailFilter !== 'all') {
      list = list.filter(e => e.sources.includes(emailFilter));
    }
    const q = emailSearch.trim().toLowerCase();
    if (q) {
      list = list.filter(e =>
        e.email.toLowerCase().includes(q) ||
        (e.nome || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [emailsData, emailFilter, emailSearch]);

  const copyEmailsToClipboard = () => {
    const text = filteredEmails.map(e => e.email).join(', ');
    navigator.clipboard.writeText(text);
    toast({ title: "Copiate!", description: `${filteredEmails.length} email copiate negli appunti` });
  };

  const sendBulkEmail = () => {
    if (filteredEmails.length === 0) {
      toast({ title: "Nessuna email", description: "Nessun destinatario nel filtro attuale", variant: "destructive" });
      return;
    }
    const CHUNK = 80; // evita URI troppo lunghi / limiti client mail
    const addresses = filteredEmails.map(e => e.email);
    const total = addresses.length;

    if (total > CHUNK) {
      const ok = window.confirm(
        `Stai per aprire ${Math.ceil(total / CHUNK)} finestre email (${total} destinatari, max ${CHUNK} per email).\n\nContinua?`
      );
      if (!ok) return;
    }

    const subject = encodeURIComponent('Tecniche Perfette — Novità');
    const body = encodeURIComponent('Ciao,\n\n\n\n—\nTecniche Perfette\ninfo@tecnicheperfette.com');

    for (let i = 0; i < total; i += CHUNK) {
      const bcc = addresses.slice(i, i + CHUNK).join(',');
      const href = `mailto:?bcc=${encodeURIComponent(bcc)}&subject=${subject}&body=${body}`;
      const w = window.open(href, '_blank');
      if (!w) {
        toast({
          title: "Popup bloccati",
          description: "Abilita i popup per questo sito per inviare email di massa",
          variant: "destructive"
        });
        return;
      }
    }
    toast({
      title: "Email preparate",
      description: `${total} destinatari in BCC distribuiti su ${Math.ceil(total / CHUNK)} email`
    });
  };

  const downloadEmailsCsv = () => {
    const header = 'email,nome,sorgenti,data\n';
    const rows = filteredEmails.map(e =>
      `"${e.email}","${(e.nome || '').replace(/"/g, '""')}","${e.sources.join('|')}","${e.created_at || ''}"`
    ).join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tp_emails_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const loadIgShortcodes = async () => {
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/instagram/featured`);
      const d = await r.json();
      const codes = d.shortcodes || [];
      const padded = [0, 1, 2, 3].map(i => codes[i] || '');
      setIgShortcodes(padded);
    } catch (e) {
      logError('[admin] impossibile caricare gli shortcode IG', e);
    }
  };

  const saveIgShortcodes = async () => {
    setIgSaving(true);
    try {
      const savedPin = sessionStorage.getItem('tp_admin_pin') || pin;
      const formData = new FormData();
      formData.append('pin', savedPin);
      formData.append('shortcodes', igShortcodes.filter(Boolean).join(','));
      formData.append('username', 'tecnicheperfette');
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/instagram/featured`, {
        method: 'POST',
        body: formData
      });
      if (!r.ok) throw new Error('fail');
      const d = await r.json();
      toast({ title: "Salvato!", description: `${d.count} post Instagram aggiornati` });
      const padded = [0, 1, 2, 3].map(i => d.shortcodes[i] || '');
      setIgShortcodes(padded);
    } catch {
      toast({ title: "Errore", description: "Impossibile salvare. PIN scaduto?", variant: "destructive" });
    } finally {
      setIgSaving(false);
    }
  };

  const loadHomeContent = async () => {
    try {
      const [rf, rs, re, rh] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/site-content/finalists`),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/site-content/shorts`),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/site-content/editions`),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/site-content/home-settings`),
      ]);
      const [df, ds, de, dh] = await Promise.all([rf.json(), rs.json(), re.json(), rh.json()]);
      setHomeFinalists(withUids(Array.isArray(df.items) ? df.items : []));
      setHomeShorts(withUids(Array.isArray(ds.items) ? ds.items : []));
      setHomeSettings(dh);
      // Se il DB è vuoto, precompila con i dati storici del sito
      setHomeEditions(withUids(Array.isArray(de.items) && de.items.length ? de.items : DEFAULT_EDITIONS));
      setHomeLoaded(true);
    } catch {
      toast({ title: "Errore", description: "Impossibile caricare i contenuti home", variant: "destructive" });
    }
  };

  const loadWall = async () => {
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/site-content/wall-of-fame`);
      const d = await r.json();
      setWallItems(withUids(Array.isArray(d.items) && d.items.length ? d.items : DEFAULT_WALL_OF_FAME));
      setWallLoaded(true);
    } catch {
      toast({ title: "Errore", description: "Impossibile caricare la Wall of Fame", variant: "destructive" });
    }
  };

  const saveWall = async () => {
    setWallSaving(true);
    try {
      const savedPin = sessionStorage.getItem('tp_admin_pin') || pin;
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/site-content/wall-of-fame`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin: savedPin, items: wallItems })
      });
      if (!r.ok) throw new Error('fail');
      const d = await r.json();
      setWallItems(withUids(d.items));
      toast({ title: "Salvato!", description: `${d.count} card Wall of Fame aggiornate` });
    } catch {
      toast({ title: "Errore", description: "Impossibile salvare. PIN scaduto?", variant: "destructive" });
    } finally {
      setWallSaving(false);
    }
  };

  const loadSponsors = async () => {
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/site-content/sponsors`);
      const d = await r.json();
      setSponsorItems(withUids(Array.isArray(d.items) && d.items.length ? d.items : DEFAULT_SPONSORS));
      setSponsorsLoaded(true);
    } catch {
      toast({ title: "Errore", description: "Impossibile caricare gli sponsor", variant: "destructive" });
    }
  };

  const saveSponsors = async () => {
    setSponsorsSaving(true);
    try {
      const savedPin = sessionStorage.getItem('tp_admin_pin') || pin;
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/site-content/sponsors`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin: savedPin, items: sponsorItems })
      });
      if (!r.ok) throw new Error('fail');
      const d = await r.json();
      setSponsorItems(withUids(d.items));
      toast({ title: "Salvato!", description: `${d.count} sponsor aggiornati` });
    } catch {
      toast({ title: "Errore", description: "Impossibile salvare. PIN scaduto?", variant: "destructive" });
    } finally {
      setSponsorsSaving(false);
    }
  };

  const saveHomeSettings = async () => {
    setHomeSaving(true);
    try {
      const savedPin = sessionStorage.getItem('tp_admin_pin') || pin;
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/site-content/home-settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin: savedPin, ...homeSettings })
      });
      if (!r.ok) throw new Error('fail');
      setHomeSettings(await r.json());
      toast({ title: "Salvato!", description: "Impostazioni home aggiornate" });
    } catch {
      toast({ title: "Errore", description: "Impossibile salvare. PIN scaduto?", variant: "destructive" });
    } finally {
      setHomeSaving(false);
    }
  };

  const saveHomeContent = async (kind) => {
    setHomeSaving(true);
    try {
      const savedPin = sessionStorage.getItem('tp_admin_pin') || pin;
      const items = kind === 'finalists' ? homeFinalists : kind === 'shorts' ? homeShorts : homeEditions;
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/site-content/${kind}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin: savedPin, items })
      });
      if (!r.ok) throw new Error('fail');
      const d = await r.json();
      if (kind === 'finalists') setHomeFinalists(withUids(d.items));
      else if (kind === 'shorts') setHomeShorts(withUids(d.items));
      else setHomeEditions(withUids(d.items));
      const labels = { finalists: 'finalisti', shorts: 'shorts', editions: 'edizioni' };
      toast({ title: "Salvato!", description: `${d.count} ${labels[kind]} aggiornati` });
    } catch {
      toast({ title: "Errore", description: "Impossibile salvare. PIN scaduto?", variant: "destructive" });
    } finally {
      setHomeSaving(false);
    }
  };

  const loadChatRooms = async () => {
    setChatLoading(true);
    try {
      const savedPin = sessionStorage.getItem('tp_admin_pin') || pin;
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/chat/rooms?pin=${encodeURIComponent(savedPin)}`);
      if (!r.ok) throw new Error('unauth');
      const d = await r.json();
      const rooms = d.rooms || [];
      setChatRooms(rooms);
      updateFaviconBadge(rooms.reduce((s, r) => s + (r.unread_count || 0), 0));
    } catch {
      toast({ title: "Errore", description: "Impossibile caricare le chat", variant: "destructive" });
    } finally {
      setChatLoading(false);
    }
  };

  const openChatRoom = async (chatId) => {
    setSelectedChatId(chatId);
    try {
      const savedPin = sessionStorage.getItem('tp_admin_pin') || pin;
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/chat/messages/${chatId}?pin=${encodeURIComponent(savedPin)}`);
      const d = await r.json();
      setChatMessages(d.messages || []);
    } catch {
      setChatMessages([]);
    }
    // Mark-read all'apertura
    try {
      const savedPin = sessionStorage.getItem('tp_admin_pin') || pin;
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/chat/mark-read/${chatId}?pin=${encodeURIComponent(savedPin)}`, { method: 'POST' });
      // Ottimistico: azzera unread per questa chat nella lista + aggiorna favicon
      setChatRooms(prev => {
        const next = prev.map(r => r.chat_id === chatId ? { ...r, unread: false, unread_count: 0 } : r);
        updateFaviconBadge(next.reduce((s, r) => s + (r.unread_count || 0), 0));
        return next;
      });
    } catch (e) {
      logError('[admin] errore aggiornamento stato chat', e);
    }
  };

  const playNotificationSound = () => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.value = 880;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.25);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.28);
      setTimeout(() => ctx.close(), 500);
    } catch {
      /* audio unavailable */
    }
  };

  const startTitleBlink = (count) => {
    if (!originalTitleRef.current) {
      originalTitleRef.current = document.title;
    }
    if (document.hasFocus()) return; // no blink se tab attiva
    clearInterval(blinkIntervalRef.current);
    let toggled = false;
    blinkIntervalRef.current = setInterval(() => {
      document.title = toggled ? originalTitleRef.current : `🔴 (${count}) Nuovo messaggio!`;
      toggled = !toggled;
    }, 1000);
  };

  const stopTitleBlink = () => {
    clearInterval(blinkIntervalRef.current);
    blinkIntervalRef.current = null;
    if (originalTitleRef.current) document.title = originalTitleRef.current;
  };

  const updateFaviconBadge = (count) => {
    try {
      if (!faviconRef.current) {
        faviconRef.current = document.querySelector('link[rel="icon"]') || document.querySelector('link[rel="shortcut icon"]');
        if (faviconRef.current && !faviconRef.current.dataset.original) {
          faviconRef.current.dataset.original = faviconRef.current.href;
        }
      }
      if (!faviconRef.current) return;

      if (count <= 0) {
        faviconRef.current.href = faviconRef.current.dataset.original || faviconRef.current.href;
        return;
      }

      const canvas = document.createElement('canvas');
      canvas.width = 64; canvas.height = 64;
      const ctx = canvas.getContext('2d');
      // Background dot (brand red)
      ctx.fillStyle = '#C41E1E';
      ctx.beginPath();
      ctx.arc(32, 32, 30, 0, 2 * Math.PI);
      ctx.fill();
      // Text
      ctx.fillStyle = '#FFFFFF';
      ctx.font = 'bold 40px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(count > 9 ? '9+' : String(count), 32, 34);
      faviconRef.current.href = canvas.toDataURL('image/png');
    } catch {
      /* ignore */
    }
  };

  const showWebNotification = (chatId, senderName, message) => {
    try {
      if (typeof Notification === 'undefined') return;
      if (Notification.permission !== 'granted') return;
      if (document.hasFocus()) return; // solo se tab non attiva

      const n = new Notification(`💬 ${senderName}`, {
        body: message.length > 100 ? message.slice(0, 100) + '…' : message,
        icon: '/favicon.ico',
        tag: `chat-${chatId}`,
        requireInteraction: false,
      });
      n.onclick = () => {
        window.focus();
        n.close();
        // Apri tab chat live + seleziona chat
        setTimeout(() => {
          document.querySelector('[data-testid="admin-tab-chat"]')?.click();
          openChatRoom(chatId);
        }, 100);
      };
      setTimeout(() => { try { n.close(); } catch { /* ignore */ } }, 8000);
    } catch {
      /* ignore */
    }
  };

  const requestNotifPermission = async () => {
    if (typeof Notification === 'undefined') {
      toast({ title: 'Non supportato', description: 'Il tuo browser non supporta le notifiche', variant: 'destructive' });
      return;
    }
    const p = await Notification.requestPermission();
    setNotifPermission(p);
    if (p === 'granted') {
      toast({ title: 'Notifiche attive', description: 'Riceverai un popup sul desktop per ogni nuovo messaggio' });
    }
  };

  const loadAnalytics = async (opts = {}) => {
    setAnalyticsLoading(true);
    try {
      const savedPin = sessionStorage.getItem('tp_admin_pin') || pin;
      const d = opts.days !== undefined ? opts.days : analyticsDays;
      const r = opts.region !== undefined ? opts.region : analyticsRegion;
      const y = opts.year !== undefined ? opts.year : analyticsYear;
      const qs = new URLSearchParams({
        pin: savedPin,
        days: String(d),
      });
      if (r) qs.append('region', r);
      if (y) qs.append('year', String(y));
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/analytics?${qs.toString()}`);
      if (!res.ok) throw new Error('unauth');
      const data = await res.json();
      setAnalyticsData(data);
    } catch {
      toast({ title: "Errore", description: "Impossibile caricare le analytics", variant: "destructive" });
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const downloadAnalyticsCsv = () => {
    const savedPin = sessionStorage.getItem('tp_admin_pin') || pin;
    const qs = new URLSearchParams({
      pin: savedPin,
      days: String(analyticsDays),
    });
    if (analyticsRegion) qs.append('region', analyticsRegion);
    if (analyticsYear) qs.append('year', String(analyticsYear));
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/analytics/export.csv?${qs.toString()}`;
    window.open(url, '_blank');
  };

  const connectAdminSocket = () => {
    if (adminSocketRef.current) return;
    const savedPin = sessionStorage.getItem('tp_admin_pin') || pin;
    const socket = socketIO(process.env.REACT_APP_BACKEND_URL, {
      transports: ['polling', 'websocket'],
      path: '/api/socket.io'
    });
    socket.on('connect', () => {
      socket.emit('join_admin_panel', { pin: savedPin });
    });
    socket.on('admin_panel_joined', () => {});
    socket.on('admin_panel_denied', () => {
      toast({ title: "Accesso chat negato", description: "PIN non valido", variant: "destructive" });
    });
    socket.on('chat_activity', (data) => {
      // Nuovo messaggio in qualche chat → refresh lista + suono + blink se da utente
      setChatRooms(prev => {
        const others = prev.filter(r => r.chat_id !== data.chat_id);
        const existing = prev.find(r => r.chat_id === data.chat_id) || { total_messages: 0, unread_count: 0 };
        const isFromUser = data.sender_type === 'user';
        const isOpenChat = data.chat_id === selectedChatId;
        const nextUnread = isFromUser && !isOpenChat ? ((existing.unread_count || 0) + 1) : (existing.unread_count || 0);
        return [{
          chat_id: data.chat_id,
          last_message: data.message,
          last_sender_type: data.sender_type,
          last_sender_name: data.sender_name,
          last_timestamp: data.timestamp,
          total_messages: (existing.total_messages || 0) + 1,
          unread_count: nextUnread,
          unread: nextUnread > 0,
        }, ...others];
      });

      // Notifica sonora + title blink + web notification + favicon badge solo su messaggio utente
      if (data.sender_type === 'user' && data.chat_id !== selectedChatId) {
        playNotificationSound();
        showWebNotification(data.chat_id, data.sender_name || 'Utente', data.message);
        // Aggiorna badge favicon + title blink con conteggio totale
        setChatRooms(current => {
          const totalUnread = current.reduce((s, r) => s + (r.unread_count || 0), 0);
          startTitleBlink(totalUnread);
          updateFaviconBadge(totalUnread);
          return current;
        });
      }
    });
    socket.on('chat_typing', (data) => {
      if (data?.by === 'user' && !data?.stopped) {
        setUserTypingChatId(data.chat_id);
        clearTimeout(userTypingTimerRef.current);
        userTypingTimerRef.current = setTimeout(() => setUserTypingChatId(null), 4000);
      } else if (data?.stopped) {
        setUserTypingChatId(null);
      }
    });
    socket.on('new_message', (msg) => {
      if (selectedChatId && msg.chat_id === selectedChatId) {
        setChatMessages(prev => [...prev, msg]);
      }
    });
    adminSocketRef.current = socket;
  };

  const sendAdminReply = () => {
    const txt = chatInput.trim();
    if (!txt || !selectedChatId || !adminSocketRef.current) return;
    adminSocketRef.current.emit('send_message', {
      chat_id: selectedChatId,
      message: txt,
      sender_type: 'admin',
      sender_name: 'Admin TP',
    });
    adminSocketRef.current.emit('stop_typing', { chat_id: selectedChatId, by: 'admin' });
    clearTimeout(adminTypingTimerRef.current);
    setChatInput('');
  };

  const handleAdminInputChange = (e) => {
    setChatInput(e.target.value);
    if (adminSocketRef.current && selectedChatId) {
      adminSocketRef.current.emit('admin_typing', { chat_id: selectedChatId });
      clearTimeout(adminTypingTimerRef.current);
      adminTypingTimerRef.current = setTimeout(() => {
        adminSocketRef.current?.emit('stop_typing', { chat_id: selectedChatId, by: 'admin' });
      }, 2500);
    }
  };

  useEffect(() => {
    const onFocus = () => stopTitleBlink();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  useEffect(() => {
    if (!authenticated) return;
    // Iscriviti alla room chat aperta
    if (adminSocketRef.current && selectedChatId) {
      adminSocketRef.current.emit('join_chat', {
        chat_id: selectedChatId,
        user_type: 'admin',
      });
    }
  }, [selectedChatId, authenticated]);

  useEffect(() => {
    return () => {
      if (adminSocketRef.current) {
        adminSocketRef.current.close();
        adminSocketRef.current = null;
      }
    };
  }, []);

  const handleApprove = async (id) => {
    setActionLoading(id);
    try {
      await approveFreestyler(id);
      toast({ title: "Approvato!", description: "Il profilo è ora visibile nella lista freestyler." });
      await loadData();
      setSelectedProfile(null);
    } catch {
      toast({ title: "Errore", description: "Impossibile approvare", variant: "destructive" });
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (id) => {
    setActionLoading(id);
    try {
      await rejectFreestyler(id);
      toast({ title: "Rifiutato", description: "Il profilo è stato rifiutato." });
      await loadData();
      setSelectedProfile(null);
    } catch {
      toast({ title: "Errore", description: "Impossibile rifiutare", variant: "destructive" });
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo profilo?")) return;
    setActionLoading(id);
    try {
      await deleteFreestyler(id);
      toast({ title: "Eliminato", description: "Il profilo è stato eliminato." });
      await loadData();
      setSelectedProfile(null);
    } catch {
      toast({ title: "Errore", description: "Impossibile eliminare", variant: "destructive" });
    } finally {
      setActionLoading(null);
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedProfile) return;
    setActionLoading(selectedProfile.id);
    try {
      await updateFreestyler(selectedProfile.id, editData);
      toast({ title: "Salvato!", description: "Le modifiche sono state salvate." });
      setEditMode(false);
      await loadData();
      // Update selected profile
      const updated = { ...selectedProfile, ...editData };
      setSelectedProfile(updated);
    } catch {
      toast({ title: "Errore", description: "Impossibile salvare le modifiche", variant: "destructive" });
    } finally {
      setActionLoading(null);
    }
  };

  const openProfile = (profile) => {
    setSelectedProfile(profile);
    setEditData({
      name: profile.name,
      nickname: profile.nickname,
      bio: profile.bio,
      luogo_crew: profile.luogo_crew || '',
      instagram: profile.instagram || '',
      tiktok: profile.tiktok || '',
    });
    setEditMode(false);
  };

  const statusColor = {
    pending: 'bg-yellow-600',
    approved: 'bg-green-600',
    rejected: 'bg-red-800'
  };

  const statusIcon = {
    pending: Clock,
    approved: CheckCircle,
    rejected: XCircle
  };

  // Login Screen
  if (!authenticated) {
    return (
      <div className="py-16 px-4 flex items-center justify-center" style={{ backgroundColor: 'var(--tp-black)', minHeight: '80vh' }}>
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: 'var(--tp-red)' }}>
              <Shield size={32} className="text-white" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '2px' }}>
              PANNELLO ADMIN
            </h1>
            <p className="text-gray-400 text-sm mt-2">Inserisci il PIN per accedere</p>
          </div>
          <form onSubmit={handleSubmit} className="p-6 rounded-lg border border-gray-800" style={{ backgroundColor: 'var(--tp-dark)' }}>
            <div className="mb-4">
              <label className="block text-gray-400 text-xs mb-1 tracking-wider" style={{ fontFamily: 'Oswald, sans-serif' }}>
                <Lock size={12} className="inline mr-1" />PIN DI ACCESSO
              </label>
              <Input
                type="password"
                value={pin}
                onChange={e => setPin(e.target.value)}
                className="bg-gray-900 border-gray-700 text-white text-center text-lg tracking-[0.5em] focus:border-red-500"
                placeholder="• • • • • •"
                autoFocus
              />
            </div>
            <Button type="submit" className="w-full py-3 text-white font-bold"
              style={{ backgroundColor: 'var(--tp-red)', fontFamily: 'Oswald, sans-serif' }}>
              ACCEDI
            </Button>
          </form>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="py-8 sm:py-12 px-4" style={{ backgroundColor: 'var(--tp-black)', minHeight: '80vh' }}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-2"
              style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '2px' }}>
              <Shield size={24} className="text-red-500" /> PANNELLO ADMIN
            </h1>
            <p className="text-gray-400 text-sm mt-1">Gestisci i profili freestyler</p>
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={loadData} variant="outline" className="border-gray-700 text-white hover:bg-gray-800 gap-2 text-sm">
              <RefreshCw size={14} /> Aggiorna
            </Button>
            <Button onClick={handleLogout} variant="outline" className="border-gray-700 text-red-400 hover:bg-red-900/30 text-sm">
              Esci
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          {[
            { label: 'In attesa', value: pending.length, color: 'text-yellow-400', bg: 'bg-yellow-900/20' },
            { label: 'Approvati', value: allProfiles.filter(p => p.status === 'approved').length, color: 'text-green-400', bg: 'bg-green-900/20' },
            { label: 'Rifiutati', value: allProfiles.filter(p => p.status === 'rejected').length, color: 'text-red-400', bg: 'bg-red-900/20' },
            { label: 'Totale', value: allProfiles.length, color: 'text-white', bg: 'bg-gray-800' },
          ].map((stat) => (
            <div key={stat.label} className={`p-4 rounded-lg ${stat.bg} text-center`}>
              <p className={`text-2xl font-bold ${stat.color}`} style={{ fontFamily: 'Oswald, sans-serif' }}>{stat.value}</p>
              <p className="text-gray-400 text-xs mt-1">{stat.label}</p>
            </div>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-16"><Loader2 size={32} className="animate-spin text-red-500 mx-auto" /></div>
        ) : (
          <Tabs defaultValue="pending" onValueChange={(v) => {
            if (v === 'emails' && !emailsData) loadEmails();
            if (v === 'instagram') loadIgShortcodes();
            if (v === 'homecontent' && !homeLoaded) loadHomeContent();
            if (v === 'wall' && !wallLoaded) loadWall();
            if (v === 'sponsors' && !sponsorsLoaded) loadSponsors();
            if (v === 'chat') { loadChatRooms(); connectAdminSocket(); }
            if (v === 'stats' && !analyticsData) loadAnalytics();
          }}>
            <TabsList className="bg-gray-800 border border-gray-700 mb-6 flex-wrap h-auto">
              <TabsTrigger value="pending" className="data-[state=active]:bg-yellow-600 data-[state=active]:text-white text-gray-400 text-xs sm:text-sm" style={{ fontFamily: 'Oswald, sans-serif' }}>
                IN ATTESA {pending.length > 0 && <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-yellow-500 text-black text-[10px] font-bold">{pending.length}</span>}
              </TabsTrigger>
              <TabsTrigger value="approved" className="data-[state=active]:bg-green-600 data-[state=active]:text-white text-gray-400 text-xs sm:text-sm" style={{ fontFamily: 'Oswald, sans-serif' }}>
                APPROVATI
              </TabsTrigger>
              <TabsTrigger value="rejected" className="data-[state=active]:bg-red-700 data-[state=active]:text-white text-gray-400 text-xs sm:text-sm" style={{ fontFamily: 'Oswald, sans-serif' }}>
                RIFIUTATI
              </TabsTrigger>
              <TabsTrigger value="all" className="data-[state=active]:bg-gray-600 data-[state=active]:text-white text-gray-400 text-xs sm:text-sm" style={{ fontFamily: 'Oswald, sans-serif' }}>
                TUTTI
              </TabsTrigger>
              <TabsTrigger data-testid="admin-tab-emails" value="emails" className="data-[state=active]:bg-red-600 data-[state=active]:text-white text-gray-400 text-xs sm:text-sm" style={{ fontFamily: 'Oswald, sans-serif' }}>
                <Mail size={14} className="inline mr-1" /> EMAIL
              </TabsTrigger>
              <TabsTrigger data-testid="admin-tab-instagram" value="instagram" className="data-[state=active]:bg-pink-600 data-[state=active]:text-white text-gray-400 text-xs sm:text-sm" style={{ fontFamily: 'Oswald, sans-serif' }}>
                <Instagram size={14} className="inline mr-1" /> INSTAGRAM
              </TabsTrigger>
              <TabsTrigger data-testid="admin-tab-homecontent" value="homecontent" className="data-[state=active]:bg-orange-600 data-[state=active]:text-white text-gray-400 text-xs sm:text-sm" style={{ fontFamily: 'Oswald, sans-serif' }}>
                <Trophy size={14} className="inline mr-1" /> HOME
              </TabsTrigger>
              <TabsTrigger data-testid="admin-tab-wall" value="wall" className="data-[state=active]:bg-yellow-600 data-[state=active]:text-black text-gray-400 text-xs sm:text-sm" style={{ fontFamily: 'Oswald, sans-serif' }}>
                <Crown size={14} className="inline mr-1" /> WALL OF FAME
              </TabsTrigger>
              <TabsTrigger data-testid="admin-tab-sponsors" value="sponsors" className="data-[state=active]:bg-emerald-700 data-[state=active]:text-white text-gray-400 text-xs sm:text-sm" style={{ fontFamily: 'Oswald, sans-serif' }}>
                <Handshake size={14} className="inline mr-1" /> SPONSOR
              </TabsTrigger>
              <TabsTrigger data-testid="admin-tab-chat" value="chat" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white text-gray-400 text-xs sm:text-sm" style={{ fontFamily: 'Oswald, sans-serif' }}>
                <MessageCircle size={14} className="inline mr-1" /> CHAT LIVE {chatRooms.reduce((s, r) => s + (r.unread_count || 0), 0) > 0 && <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-red-500 text-white text-[10px] font-bold">{chatRooms.reduce((s, r) => s + (r.unread_count || 0), 0)}</span>}
              </TabsTrigger>
              <TabsTrigger data-testid="admin-tab-stats" value="stats" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-gray-400 text-xs sm:text-sm" style={{ fontFamily: 'Oswald, sans-serif' }}>
                <BarChart3 size={14} className="inline mr-1" /> STATS
              </TabsTrigger>
            </TabsList>

            {['pending', 'approved', 'rejected', 'all'].map(tab => (
              <TabsContent key={tab} value={tab}>
                <ProfileList
                  profiles={tab === 'all' ? allProfiles : allProfiles.filter(p => p.status === tab)}
                  onOpen={openProfile}
                  statusColor={statusColor}
                  statusIcon={statusIcon}
                  emptyMessage={tab === 'pending' ? 'Nessun profilo in attesa di approvazione' : `Nessun profilo ${tab === 'approved' ? 'approvato' : tab === 'rejected' ? 'rifiutato' : ''}`}
                  search={freestylerSearch}
                  setSearch={setFreestylerSearch}
                  crewFilter={freestylerCrewFilter}
                  setCrewFilter={setFreestylerCrewFilter}
                />
              </TabsContent>
            ))}

            <TabsContent value="emails">
              <EmailsPanel
                data={emailsData}
                loading={emailsLoading}
                onRefresh={loadEmails}
                filter={emailFilter}
                setFilter={setEmailFilter}
                search={emailSearch}
                setSearch={setEmailSearch}
                filtered={filteredEmails}
                onCopy={copyEmailsToClipboard}
                onDownload={downloadEmailsCsv}
                onBulkEmail={sendBulkEmail}
                pin={sessionStorage.getItem('tp_admin_pin') || pin}
              />
            </TabsContent>

            <TabsContent value="instagram">
              <InstagramPanel
                shortcodes={igShortcodes}
                setShortcodes={setIgShortcodes}
                onSave={saveIgShortcodes}
                onReload={loadIgShortcodes}
                saving={igSaving}
              />
            </TabsContent>

            <TabsContent value="homecontent">
              <HomeContentPanel
                finalists={homeFinalists}
                setFinalists={setHomeFinalists}
                shorts={homeShorts}
                setShorts={setHomeShorts}
                editions={homeEditions}
                setEditions={setHomeEditions}
                homeSettings={homeSettings}
                setHomeSettings={setHomeSettings}
                onSaveSettings={saveHomeSettings}
                onSave={saveHomeContent}
                onReload={loadHomeContent}
                saving={homeSaving}
                loaded={homeLoaded}
              />
            </TabsContent>

            <TabsContent value="wall">
              <WallOfFamePanel items={wallItems} setItems={setWallItems} onSave={saveWall} onReload={loadWall} saving={wallSaving} loaded={wallLoaded} />
            </TabsContent>

            <TabsContent value="sponsors">
              <SponsorsPanel items={sponsorItems} setItems={setSponsorItems} onSave={saveSponsors} onReload={loadSponsors} saving={sponsorsSaving} loaded={sponsorsLoaded} />
            </TabsContent>

            <TabsContent value="chat">
              <ChatLivePanel
                rooms={chatRooms}
                loading={chatLoading}
                onRefresh={loadChatRooms}
                selectedChatId={selectedChatId}
                onSelectChat={openChatRoom}
                onBackToList={() => setSelectedChatId(null)}
                messages={chatMessages}
                input={chatInput}
                onInputChange={handleAdminInputChange}
                onSend={sendAdminReply}
                userTypingChatId={userTypingChatId}
                search={chatSearch}
                setSearch={setChatSearch}
                notifPermission={notifPermission}
                onRequestNotif={requestNotifPermission}
              />
            </TabsContent>

            <TabsContent value="stats">
              <StatsPanel
                data={analyticsData}
                loading={analyticsLoading}
                onRefresh={() => loadAnalytics()}
                days={analyticsDays}
                setDays={(d) => { setAnalyticsDays(d); loadAnalytics({ days: d }); }}
                region={analyticsRegion}
                setRegion={(r) => { setAnalyticsRegion(r); loadAnalytics({ region: r }); }}
                year={analyticsYear}
                setYear={(y) => { setAnalyticsYear(y); loadAnalytics({ year: y }); }}
                onExportCsv={downloadAnalyticsCsv}
              />
            </TabsContent>
          </Tabs>
        )}
      </div>

      {/* Profile Detail Dialog */}
      <Dialog open={!!selectedProfile} onOpenChange={(open) => { if (!open) { setSelectedProfile(null); setEditMode(false); } }}>
        <DialogContent aria-describedby={undefined} className="bg-gray-900 border-gray-700 text-white max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedProfile && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <DialogTitle className="text-xl" style={{ fontFamily: 'Oswald, sans-serif' }}>
                    {editMode ? 'MODIFICA PROFILO' : 'DETTAGLIO PROFILO'}
                  </DialogTitle>
                  <Badge className={`${statusColor[selectedProfile.status]} text-white text-xs`}>
                    {selectedProfile.status === 'pending' ? 'In attesa' : selectedProfile.status === 'approved' ? 'Approvato' : 'Rifiutato'}
                  </Badge>
                </div>
              </DialogHeader>

              <div className="mt-3 space-y-4">
                {/* Image */}
                {selectedProfile.image ? (
                  <img src={selectedProfile.image} alt={selectedProfile.name} className="w-full h-40 object-cover rounded-lg" />
                ) : (
                  <div className="w-full h-28 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'var(--tp-gray)' }}>
                    <User size={40} className="text-gray-600" />
                  </div>
                )}

                {editMode ? (
                  /* Edit Mode */
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-gray-400 text-xs mb-1" style={{ fontFamily: 'Oswald, sans-serif' }}>NOME</label>
                        <Input value={editData.name} onChange={e => setEditData(p => ({ ...p, name: e.target.value }))}
                          className="bg-gray-800 border-gray-600 text-white focus:border-red-500" />
                      </div>
                      <div>
                        <label className="block text-gray-400 text-xs mb-1" style={{ fontFamily: 'Oswald, sans-serif' }}>NICKNAME</label>
                        <Input value={editData.nickname} onChange={e => setEditData(p => ({ ...p, nickname: e.target.value }))}
                          className="bg-gray-800 border-gray-600 text-white focus:border-red-500" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-gray-400 text-xs mb-1" style={{ fontFamily: 'Oswald, sans-serif' }}>
                        LUOGO E CREW ({editData.luogo_crew.length}/30)
                      </label>
                      <Input value={editData.luogo_crew}
                        onChange={e => { if (e.target.value.length <= 30) setEditData(p => ({ ...p, luogo_crew: e.target.value })); }}
                        className="bg-gray-800 border-gray-600 text-white focus:border-red-500" maxLength={30} />
                    </div>
                    <div>
                      <label className="block text-gray-400 text-xs mb-1" style={{ fontFamily: 'Oswald, sans-serif' }}>BIO</label>
                      <Textarea value={editData.bio} onChange={e => setEditData(p => ({ ...p, bio: e.target.value }))}
                        className="bg-gray-800 border-gray-600 text-white focus:border-red-500 min-h-[100px]" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-gray-400 text-xs mb-1" style={{ fontFamily: 'Oswald, sans-serif' }}>INSTAGRAM</label>
                        <Input value={editData.instagram} onChange={e => setEditData(p => ({ ...p, instagram: e.target.value }))}
                          className="bg-gray-800 border-gray-600 text-white focus:border-red-500" />
                      </div>
                      <div>
                        <label className="block text-gray-400 text-xs mb-1" style={{ fontFamily: 'Oswald, sans-serif' }}>TIKTOK</label>
                        <Input value={editData.tiktok} onChange={e => setEditData(p => ({ ...p, tiktok: e.target.value }))}
                          className="bg-gray-800 border-gray-600 text-white focus:border-red-500" />
                      </div>
                    </div>
                    <div className="flex gap-2 pt-2">
                      <Button onClick={handleSaveEdit} disabled={actionLoading === selectedProfile.id}
                        className="flex-1 text-white font-bold text-sm gap-2"
                        style={{ backgroundColor: 'var(--tp-red)', fontFamily: 'Oswald, sans-serif' }}>
                        {actionLoading === selectedProfile.id ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                        SALVA MODIFICHE
                      </Button>
                      <Button onClick={() => setEditMode(false)} variant="outline"
                        className="border-gray-600 text-gray-300 hover:bg-gray-800 text-sm">
                        Annulla
                      </Button>
                    </div>
                  </div>
                ) : (
                  /* View Mode */
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <InfoField label="Nome" value={selectedProfile.name} />
                      <InfoField label="Nickname" value={selectedProfile.nickname} />
                    </div>
                    {selectedProfile.luogo_crew && (
                      <InfoField label="Luogo / Crew" value={selectedProfile.luogo_crew} icon={<MapPin size={12} className="text-red-500" />} />
                    )}
                    <InfoField label="Bio" value={selectedProfile.bio} multiline />
                    <div className="grid grid-cols-2 gap-3">
                      <InfoField label="Instagram" value={selectedProfile.instagram || 'Non fornito'} />
                      <InfoField label="TikTok" value={selectedProfile.tiktok || 'Non fornito'} />
                    </div>
                    <InfoField label="Data iscrizione" value={new Date(selectedProfile.created_at).toLocaleString('it-IT')} />
                  </div>
                )}

                {/* Action Buttons */}
                {!editMode && (
                  <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-700">
                    <Button onClick={() => setEditMode(true)}
                      variant="outline" className="border-gray-600 text-white hover:bg-gray-800 gap-1 text-xs sm:text-sm flex-1 sm:flex-none"
                      style={{ fontFamily: 'Oswald, sans-serif' }}>
                      <Edit3 size={14} /> MODIFICA
                    </Button>
                    {selectedProfile.status === 'pending' && (
                      <>
                        <Button onClick={() => handleApprove(selectedProfile.id)}
                          disabled={actionLoading === selectedProfile.id}
                          className="bg-green-600 hover:bg-green-700 text-white gap-1 text-xs sm:text-sm flex-1 sm:flex-none"
                          style={{ fontFamily: 'Oswald, sans-serif' }}>
                          {actionLoading === selectedProfile.id ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                          APPROVA
                        </Button>
                        <Button onClick={() => handleReject(selectedProfile.id)}
                          disabled={actionLoading === selectedProfile.id}
                          className="bg-red-800 hover:bg-red-900 text-white gap-1 text-xs sm:text-sm flex-1 sm:flex-none"
                          style={{ fontFamily: 'Oswald, sans-serif' }}>
                          <X size={14} /> RIFIUTA
                        </Button>
                      </>
                    )}
                    {selectedProfile.status === 'rejected' && (
                      <Button onClick={() => handleApprove(selectedProfile.id)}
                        disabled={actionLoading === selectedProfile.id}
                        className="bg-green-600 hover:bg-green-700 text-white gap-1 text-xs sm:text-sm"
                        style={{ fontFamily: 'Oswald, sans-serif' }}>
                        <Check size={14} /> APPROVA
                      </Button>
                    )}
                    {selectedProfile.status === 'approved' && (
                      <Button onClick={() => handleReject(selectedProfile.id)}
                        disabled={actionLoading === selectedProfile.id}
                        className="bg-yellow-600 hover:bg-yellow-700 text-white gap-1 text-xs sm:text-sm"
                        style={{ fontFamily: 'Oswald, sans-serif' }}>
                        <AlertTriangle size={14} /> SOSPENDI
                      </Button>
                    )}
                    <Button onClick={() => handleDelete(selectedProfile.id)}
                      disabled={actionLoading === selectedProfile.id}
                      variant="outline" className="border-red-800 text-red-400 hover:bg-red-900/30 gap-1 text-xs sm:text-sm"
                      style={{ fontFamily: 'Oswald, sans-serif' }}>
                      <Trash2 size={14} /> ELIMINA
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;
